/**
 * @file components/MoreStories/component.js
 * @copyright 2016 CBC/Radio-Canada. All rights reserved.
 */
import { useEffect } from "react";
import PropTypes from "prop-types";
import ContentList from "../ContentList/component";
import ContentGrid from "../ContentGrid/component";
import { SectionHeading } from "../Heading/component";
import { sanitizeLinkTracking } from "../../utils/analytics/library";
import LowDensityPolicy from "../ContentArea/library/LowDensityPolicy";
import { DEFAULT_SECTION } from "../../constants/content";
import { MORE_CBC_STORIES_MAX_COUNT } from "../../constants/content";
import { fetch_sectional_content_graphql } from "../../actions/SectionalContent/dispatchers";
import { useDispatch, useSelector } from "react-redux";
import DisplayPolicy from "../ContentArea/library/DisplayPolicy";
import { DISPLAY_TYPES } from "../ContentList/constants";
import Title from "../Title/component";
import { default_CardRegular } from "../ContentGrid/gridLayouts/FLP_gridLayouts";
import contentGridStyles from "../ContentGrid/styles/themes/default.module.scss";
import { transformForLayouts } from "../../utils/dataTransform/ContentQueryWidget/library";
import DetailDiscoveryPlaceholder from "../../pages/Detail/components/Discovery/components/DetailDiscoveryPlaceholder/component";
import LayoutFactory from "../LayoutFactory/component";
import { TRENDING_SWIMLANE_LAYOUT } from "../LayoutFactory/constants";

const createContentProps = (
    items,
    displayPolicy,
    cardClickHandler,
    section
) => {
    return {
        items,
        displayPolicy,
        loadMore: false,
        trackingSection: `contentlistmore${sanitizeLinkTracking(section)}`,
        cardClickHandler: cardClickHandler,
    };
};

/**
 * This component displays the &#34;More CBC News&#34; section on story pages
 */

const MoreStories = ({
    fetchSection,
    section = DEFAULT_SECTION,
    itemCount = MORE_CBC_STORIES_MAX_COUNT,
    displayPolicy = new LowDensityPolicy(),
    cardClickHandler = () => {},
    contentListClasses,
    displayType = DISPLAY_TYPES.TYPE_DEFAULT,
    customTitleBar,
    headline,
    headlineLink,
    customContentList,
    externalLink,
    useGrid,
}) => {
    const sectionalContent = useSelector((state) => state.sectionalContent);
    const dispatch = useDispatch();

    useEffect(() => {
        if (fetchSection) {
            dispatch(fetch_sectional_content_graphql(section, itemCount));
        }
    }, [section]);

    const renderStoryList = (sectionList) => {
        let contentList = null;
        let headlineContent = null;
        let customHeadline = null;
        // Render the sectional list
        if (sectionList) {
            contentList = useGrid ? (
                <ContentGrid
                    gridLayout={default_CardRegular}
                    gridData={sectionList.slice(0, itemCount)}
                    cardClickHandler={cardClickHandler}
                />
            ) : (
                <ContentList
                    {...{
                        ...createContentProps(
                            sectionList.slice(0, itemCount),
                            displayPolicy,
                            cardClickHandler,
                            section
                        ),
                        contentListClasses,
                        displayType,
                    }}
                />
            );
        }
        if (customTitleBar && Object.keys(customTitleBar).length > 0) {
            customHeadline = (
                <div className="moreStoriesSubHeadline">{customTitleBar}</div>
            );
        }

        if (headline && headline !== "") {
            headlineContent = (
                <SectionHeading
                    text={headline}
                    link={headlineLink}
                    external={externalLink}
                />
            );
        }

        return (
            <div
                className={`moreStories sclt-morenews ${contentGridStyles.moreStories}`}
            >
                <section className="moreStoriesList">
                    {headlineContent}
                    {customHeadline}
                    {contentList}
                </section>
            </div>
        );
    };
    let sectionContentObject = null;
    let sectionList = null;

    if (!fetchSection && customContentList) {
        sectionList = customContentList;
    } else {
        const contentItems = sectionalContent;
        if (contentItems) {
            sectionContentObject = contentItems[section];
        }

        sectionList =
            sectionContentObject && sectionContentObject.sectionList
                ? sectionContentObject.sectionList
                : null;
    }
    if (!sectionList) return null;
    return renderStoryList(sectionList);
};

const DetailMoreStories = ({ useSectionHeading }) => {
    const contentItems = useSelector(getContentListDetail);
    const path = useSelector((state) => state.app.path);
    const isLoaded = useSelector(
        (state) => state.sectionalContent[DEFAULT_SECTION]?.isLoaded
    );

    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(
            fetch_sectional_content_graphql(
                DEFAULT_SECTION,
                MORE_CBC_STORIES_MAX_COUNT
            )
        );
    }, []);

    if (!isLoaded) {
        return (
            <div className="moreStories sclt-morenews">
                <DetailDiscoveryPlaceholder
                    numLoadingCards={MORE_CBC_STORIES_MAX_COUNT}
                />
            </div>
        );
    }

    if (contentItems?.length === 0) {
        return null;
    }

    const transformedContentList = transformForLayouts(
        contentItems,
        TRENDING_SWIMLANE_LAYOUT,
        path
    );

    const headingText = "Discover More from CBC";
    const heading = useSectionHeading ? (
        <SectionHeading text={headingText} />
    ) : (
        <Title level="h2">{headingText}</Title>
    );

    return (
        <div className="moreStories sclt-morenews" data-testid="moreStories">
            {heading}
            <LayoutFactory
                layout={TRENDING_SWIMLANE_LAYOUT}
                items={transformedContentList}
            />
        </div>
    );
};

const getContentListDetail = (state) => {
    let list = state.sectionalContent[DEFAULT_SECTION];
    if (!list) {
        return [];
    }
    list = list.sectionList;
    if (!Array.isArray(list)) {
        return [];
    }
    return list;
};

MoreStories.propTypes = {
    section: PropTypes.string,
    itemCount: PropTypes.number,
    headline: PropTypes.string,
    headlineLink: PropTypes.string,
    externalLink: PropTypes.bool,
    cardClickHandler: PropTypes.func,
    fetchSection: PropTypes.bool,
    customTitleBar: PropTypes.object,
    customContentList: PropTypes.array,
    displayPolicy: PropTypes.instanceOf(DisplayPolicy),
    contentListClasses: PropTypes.string,
    displayType: PropTypes.oneOf(Object.values(DISPLAY_TYPES)),
};

export default MoreStories;
export { DetailMoreStories };
